import React from 'react';
import { TextInput, View } from 'react-native';
import {
  KeyboardAwareScrollView,
  KeyboardStickyView,
} from 'react-native-keyboard-controller';
import { withUnistyles } from 'react-native-unistyles';

import { Avatar, Button, Heading } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { withBottomSheetInput } from '@/utils/withBottomSheetInput';

import { styles } from './styles';
import type { AppointmentRatingMessageStepProps } from './types';

const ThemedTextInput = withUnistyles(TextInput);
const BottomSheetThemedTextInput = withBottomSheetInput(ThemedTextInput);

const AppointmentRatingMessageStep: React.FC<
  AppointmentRatingMessageStepProps
> = ({
  professionalPhotoUrl,
  professionalFirstName,
  reviewText,
  onReviewTextChange,
  selectedRating,
  onSubmit,
  isSubmitting,
  pinActionButton = true,
}) => {
  const { t } = useScreenTranslations('AppointmentRatingScreen');

  const hasReview = reviewText.trim().length > 0;

  const avatar = (
    <Avatar
      uri={professionalPhotoUrl}
      size="xl"
      shape="square"
      fallbackText={professionalFirstName}
      style={styles.avatarContainer}
    />
  );

  const heading = (
    <Heading
      variant="l"
      accessibilityLabel={t('step2.heading')}
      numberOfLines={3}
      ellipsizeMode="tail"
      style={styles.mainHeading}
    >
      {t('step2.heading')}
    </Heading>
  );

  // Registers focus with the sheet's keyboard state so keyboardBehavior=
  // "interactive" (BottomSheet.tsx) pushes the sheet above the keyboard. A
  // plain TextInput never reports that, so the field stays hidden behind it.
  const InputComponent = pinActionButton
    ? ThemedTextInput
    : BottomSheetThemedTextInput;

  const input = (
    <InputComponent
      style={[
        styles.reviewInput,
        !pinActionButton ? styles.reviewInputCompact : undefined,
      ]}
      placeholder={t('step2.inputPlaceholder')}
      uniProps={(theme) => ({
        placeholderTextColor: theme.textTertiary,
      })}
      value={reviewText}
      onChangeText={onReviewTextChange}
      multiline
      maxLength={500}
      editable={!isSubmitting}
      accessibilityLabel={t('step2.inputPlaceholder')}
    />
  );

  const actionButton = (
    <Button
      variant={hasReview ? 'primary' : 'secondary'}
      size="medium"
      onPress={onSubmit}
      loading={isSubmitting}
      disabled={isSubmitting}
      style={styles.actionButton}
      accessibilityLabel={
        hasReview
          ? t('accessibility.submitButton')
          : t('accessibility.skipButton')
      }
      accessibilityHint={
        hasReview && selectedRating
          ? t('accessibility.submitButtonHint', { count: selectedRating })
          : t('accessibility.skipButtonHint')
      }
    >
      {hasReview ? t('step2.submitButton') : t('step2.skipButton')}
    </Button>
  );

  // BottomSheet variant: render in normal flow. The sheet already provides its
  // own scroll view and keyboard handling, so we skip KeyboardAwareScrollView.
  if (!pinActionButton) {
    return (
      <View style={styles.flowContent}>
        {avatar}
        {heading}
        {input}
        {actionButton}
      </View>
    );
  }

  // Fullscreen variant: keyboard-aware scroll; a flex spacer anchors the button
  // at the bottom in normal flow (no absolute positioning).
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={styles.centeredContent}
      bounces={false}
      showsVerticalScrollIndicator={false}
    >
      <KeyboardStickyView offset={{ opened: 200 }}>
        {avatar}
        {heading}
        {input}
      </KeyboardStickyView>
      <View style={styles.spacer} />
      {actionButton}
    </KeyboardAwareScrollView>
  );
};

AppointmentRatingMessageStep.displayName = 'AppointmentRatingMessageStep';
export { AppointmentRatingMessageStep };
